import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button";
import TextInput from "@/components/forms/TextInput/TextInput";
import TextLogo from "@/components/TextLogo/TextLogo";
import { useUserContext } from "@/contexts/UserContext";
import { navigate } from "wouter/use-browser-location";
import { useEffect, useState } from "react";
import { IconArrowLeft } from "@/components/icons/IconArrow";
import { changeEmail } from "@/api/auth";

import styles from "./ChangeEmailPage.module.css";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { ErrorCode } from "@boxem/core-api-schema";


export interface ChangeEmailPageProps {
  onGoBack?: () => void;
  onEmailChanged?: () => void;
}

export function ChangeEmailPage(props: ChangeEmailPageProps) {
  const { onGoBack, onEmailChanged } = props;

  const { showNotification } = useNotificationContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { user } = useUserContext();

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [confirmEmail, setConfirmEmail] = useState<string>("");
  const [confirmEmailError, setConfirmEmailError] = useState<string>("");
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      navigate("~/auth/login");
    }
  }, [user]);

  return (
    <Flex className={styles.page} direction="column" justify="space-between" align="center">
      {onGoBack && (
        <Button
          type="text"
          className={styles.backButton}
          onClick={onGoBack}
        >
          <IconArrowLeft/>
          Go Back
        </Button>
      )}

      <Flex direction="column" gap={4} grow={1} className={styles.form}>
        <TextLogo className={styles.logo} />

        <h3>Change Your Email</h3>

        <Flex direction="column" gap={1}>
          <label>New Email:</label>
          <TextInput
            placeholder="Enter your new email"
            disabled={loading}
            value={email}
            onChange={v => { setButtonDisabled(false); setEmail(v); }}
            maxLength={255}
          />
          {emailError && <span className={styles.invalid}>{emailError}</span>}
        </Flex>

        <Flex direction="column" gap={1}>
          <label>Confirm New Email:</label>
          <TextInput
            placeholder="Confirm your new email"
            disabled={loading}
            value={confirmEmail}
            onChange={v => { setButtonDisabled(false); setConfirmEmail(v); }}
            maxLength={255}
          />
          {confirmEmailError && <span className={styles.invalid}>{confirmEmailError}</span>}
        </Flex>

        <Button
          style={{marginTop: "2rem"}}
          type="primary"
          disabled={buttonDisabled || loading}
          onClick={() => {
            let valid = true;
            if (email.length === 0) {
              setEmailError("Email is empty");
              valid = false;
            }
            if (confirmEmail.length === 0) {
              setConfirmEmailError("Confirm Email is empty");
              valid = false;
            }
            if (email !== confirmEmail) {
              setConfirmEmailError("Email and confirm email do not match");
              valid = false;
            }
            if (email === user.email) {
              setEmailError("Your email already is this!");
              valid = false;
            }
            if (!valid) {
              setButtonDisabled(true);
              return;
            }
            setLoading(true);
            changeEmail({ email })
              .then(() => {
                showNotification("Changed email successfully! Check your email for a verification code", NotificationType.SUCCESS);
                onEmailChanged?.();
              })
              .catch(e => {
                if (handleErrorResponseGenerically(e)) {}
                if (e?.[0]?.type === ErrorCode.CHANGE_EMAIL_EMAIL_ALREADY_EXISTS) {
                  showNotification("Email is invalid or already exists!", NotificationType.ERROR);
                }
              })
              .finally(() => {
                setLoading(false);
              });
          }}
        >
          {loading && <LoadingSpinner/>}
          Change Email
        </Button>
      </Flex>

      <Flex gap={2}>
        <span className="small-text">© Boxem</span>
        <span className="small-text">
          <a href="https://www.boxem.com/privacypolicy" target="_blank">Privacy</a>
        </span>
        <span className="small-text">
          <a href="https://www.boxem.com/termsandconditions" target="_blank">Terms</a>
        </span>
      </Flex>
    </Flex>
  );
}
